import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_TAGS } from '@/shared/api';
import {
  createCompetency,
  deleteCompetency,
  fetchCompetenciesForRule,
  fetchCompetencyTree,
  linkRules,
  reorderCompetencies,
  updateCompetency,
} from './api';

export const coachingKeys = {
  all: [API_TAGS.COACHING] as const,
  tree: () => [...coachingKeys.all, 'tree'] as const,
  forRule: (ruleId: number) => [...coachingKeys.all, 'by-rule', ruleId] as const,
};

export const useCompetencyTree = () => {
  return useQuery({
    queryKey: coachingKeys.tree(),
    queryFn: fetchCompetencyTree,
  });
};

export const useCreateCompetency = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCompetency,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: coachingKeys.tree() });
    },
  });
};

export const useUpdateCompetency = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCompetency,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: coachingKeys.tree() });
    },
  });
};

export const useDeleteCompetency = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteCompetency,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: coachingKeys.tree() });
      queryClient.invalidateQueries({ queryKey: coachingKeys.all });
    },
  });
};

export const useLinkRules = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: linkRules,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: coachingKeys.all });
    },
  });
};

export const useReorderCompetencies = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: reorderCompetencies,
    onError: () => {
      queryClient.invalidateQueries({ queryKey: coachingKeys.tree() });
    },
  });
};

export const useCompetenciesForRule = (ruleId?: number) => {
  return useQuery({
    queryKey: coachingKeys.forRule(ruleId!),
    queryFn: () => fetchCompetenciesForRule(ruleId!),
    enabled: typeof ruleId === 'number' && ruleId > 0,
  });
};
